import { useEffect, useState, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { getFirebaseAuth } from '../utils/firebase';
import { setStoredLang } from '../i18n';
import { getStoredAuth } from '../contexts/CustomerAuthContext';
import { usePwaNav } from '../contexts/PwaNavContext';
import type { Order, UpsellItem, VenueOrder, ProductOptionGroup } from '../types/order';

const PRODUCTS_CACHE_TTL = 60_000;
const POLL_INTERVAL = 5_000;

function triggerReadyEffect() {
  if ('vibrate' in navigator) {
    navigator.vibrate([100, 80, 200, 80, 200]);
  }
  // Dynamic import to avoid calling confetti in hooks file with static import
  import('canvas-confetti').then(({ default: confetti }) => {
    const defaults = {
      origin: { y: 0.75 },
      colors: ['#FF5A00', '#F59E0B', '#FCD34D', '#FF8C42', '#FAF7F2', '#ffffff'],
      zIndex: 9999,
    };
    confetti({ ...defaults, particleCount: 60, angle: 60, spread: 55, origin: { x: 0, y: 0.75 } });
    setTimeout(() => {
      confetti({ ...defaults, particleCount: 60, angle: 120, spread: 55, origin: { x: 1, y: 0.75 } });
    }, 150);
  });
}

function triggerPreparingEffect() {
  if ('vibrate' in navigator) {
    navigator.vibrate(60);
  }
}

interface UseOrderSocketOptions {
  debugMode: boolean;
  isAuthenticated: boolean;
  navigate: (path: string) => void;
  addDebug: (msg: string) => void;
  searchParams: URLSearchParams;
  onPaymentConfirmed: (data: { amount: number }) => void;
  onPaymentFailed: (reason: string) => void;
}

interface DebugInfo {
  lastFetch: string;
  apiUrl: string;
  productPriorities?: { name: string; upsellPriority: number }[];
  prioritiesError?: string;
  upsellsSource?: 'products' | 'order';
  productsTotal?: number;
  productsWithPriority?: number;
  productsPassedFilter?: number;
}

interface UseOrderSocketReturn {
  order: Order | null;
  setOrder: React.Dispatch<React.SetStateAction<Order | null>>;
  loading: boolean;
  loadError: string | null;
  isOffline: boolean;
  upsellsLoading: boolean;
  debugInfo: DebugInfo | null;
  statusChanged: string | null;
  showReadyFlash: boolean;
  setShowReadyFlash: React.Dispatch<React.SetStateAction<boolean>>;
  showPreparingModal: boolean;
  setShowPreparingModal: React.Dispatch<React.SetStateAction<boolean>>;
  repeatItems: Array<{ productId: string; name: string; imageUrl: string | null; price: number }>;
  socialProofCount: number;
  venueOrders: VenueOrder[];
  setVenueOrders: React.Dispatch<React.SetStateAction<VenueOrder[]>>;
  venueDetailId: string | null;
  setVenueDetailId: React.Dispatch<React.SetStateAction<string | null>>;
  venueDetailData: { items: { product: { name: string }; quantity: number; price: number }[]; restaurantName: string; status: string } | null;
  setVenueDetailData: React.Dispatch<React.SetStateAction<{ items: { product: { name: string }; quantity: number; price: number }[]; restaurantName: string; status: string } | null>>;
  venueDetailLoading: boolean;
  setVenueDetailLoading: React.Dispatch<React.SetStateAction<boolean>>;
  showRefundSheet: boolean;
  setShowRefundSheet: React.Dispatch<React.SetStateAction<boolean>>;
  refundAmount: number | null;
  socketRef: React.MutableRefObject<ReturnType<typeof io> | null>;
  handleRefresh: () => void;
  fetchOrder: (socket?: ReturnType<typeof io> | null) => Promise<void>;
  openVenueDetail: (vo: VenueOrder) => Promise<void>;
}

/**
 * Merge an incoming `order-updated` socket payload onto the current order.
 * Status-change emits (especially POS-driven ones) can be "thin" — scalar fields
 * only, without the items/restaurant/customer relations. Never let those drop to
 * undefined, or the render (`order.items.reduce(...)`) crashes the whole page.
 * Keep the relations from `prev` when the payload omits them.
 */
export function mergeOrderUpdate(prev: Order | null, o: Order): Order | null {
  if (!prev) return o.items ? o : prev; // ignore a thin update that arrives before the first full load
  return {
    ...prev,
    ...o,
    items: o.items ?? prev.items,
    restaurant: o.restaurant ?? prev.restaurant,
    upsells: prev.upsells?.length ? prev.upsells : o.upsells,
    recommendations: o.recommendations?.r1 ? o.recommendations : prev.recommendations,
    offers: o.offers ?? prev.offers,
  };
}

export function useOrderSocket(
  qrCode: string | undefined,
  options: UseOrderSocketOptions
): UseOrderSocketReturn {
  const { debugMode, isAuthenticated, navigate, addDebug, searchParams, onPaymentConfirmed, onPaymentFailed } = options;

  // Track whether the order overlay is still the active page.
  // If user navigates to passport/history/etc while an in-flight fetchOrder is
  // resolving, we must NOT redirect them back to home.
  const { showOrder } = usePwaNav();
  const isActiveRef = useRef(showOrder);
  isActiveRef.current = showOrder;

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [upsellsLoading, setUpsellsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [showReadyFlash, setShowReadyFlash] = useState(false);
  const [showPreparingModal, setShowPreparingModal] = useState(false);
  const [statusChanged, setStatusChanged] = useState<string | null>(null);
  const [repeatItems, setRepeatItems] = useState<Array<{ productId: string; name: string; imageUrl: string | null; price: number }>>([]);
  const [socialProofCount, setSocialProofCount] = useState(0);

  const [showRefundSheet, setShowRefundSheet] = useState(false);
  const [refundAmount, setRefundAmount] = useState<number | null>(null);

  const [venueOrders, setVenueOrders] = useState<VenueOrder[]>([]);
  const [venueDetailId, setVenueDetailId] = useState<string | null>(null);
  const [venueDetailData, setVenueDetailData] = useState<{ items: { product: { name: string }; quantity: number; price: number }[]; restaurantName: string; status: string } | null>(null);
  const [venueDetailLoading, setVenueDetailLoading] = useState(false);

  const prevStatusRef = useRef<string | null>(null);
  const orderIdRef = useRef<string | null>(null);
  const restaurantIdRef = useRef<string | null>(null);
  const socketRef = useRef<ReturnType<typeof io> | null>(null);
  const fetchingRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);
  const productsCacheRef = useRef<{
    rid: string;
    upsells: UpsellItem[];
    ts: number;
    debugMeta?: { total: number; withPriority: number; passed: number };
  } | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  // Stable ref to fetchOrder — lets the online listener call the latest version
  const fetchOrderRef = useRef<((socket?: ReturnType<typeof io> | null) => void) | null>(null);

  // Venue orders polling — use ref to avoid stale closure
  const venueOrdersRef = useRef<VenueOrder[]>([]);
  venueOrdersRef.current = venueOrders;

  // Claim order ref
  const claimedRef = useRef(false);

  // Reset state when qrCode changes (e.g. scanning a new QR while overlay is open)
  useEffect(() => {
    setOrder(null);
    setLoading(true);
    setUpsellsLoading(true);
    setLoadError(null);
    setStatusChanged(null);
    prevStatusRef.current = null;
    orderIdRef.current = null;
    fetchingRef.current = false;
    hasLoadedOnceRef.current = false;
    claimedRef.current = false;
  }, [qrCode]);

  const fetchProducts = useCallback(async (
    rid: string,
    maxUpsells: number
  ): Promise<{ upsells: UpsellItem[]; source: 'products' | 'order'; total?: number; withPriority?: number; passed?: number } | null> => {
    const cache = productsCacheRef.current;
    if (cache && cache.rid === rid && Date.now() - cache.ts < PRODUCTS_CACHE_TTL) {
      return {
        upsells: cache.upsells,
        source: 'products',
        total: cache.debugMeta?.total,
        withPriority: cache.debugMeta?.withPriority,
        passed: cache.debugMeta?.passed,
      };
    }
    try {
      const base = API_URL || '';
      const { data: products } = await axios.get(`${base}/api/products/restaurant/${rid}?_=${Date.now()}`);
      const allProducts = products || [];
      const withPriority = allProducts.filter((p: { upsellPriority?: number }) => (p.upsellPriority ?? 0) > 0);
      const upsellProducts = allProducts
        .filter(
          (p: { id: string; upsellPriority?: number; stockToday?: number | null; isActive?: boolean }) =>
            (p.upsellPriority ?? 0) > 0 &&
            (p.isActive !== false) &&
            (p.stockToday == null || p.stockToday > 0)
        )
        .sort((a: { upsellPriority?: number }, b: { upsellPriority?: number }) => (b.upsellPriority ?? 0) - (a.upsellPriority ?? 0))
        .slice(0, maxUpsells);
      const upsells = upsellProducts.map((p: {
        id: string;
        name: string;
        description?: string;
        price: number;
        category?: string;
        imageUrl?: string;
        stockToday?: number;
        isEditable?: boolean;
        optionGroups?: ProductOptionGroup[];
      }) => ({
        id: p.id,
        product: {
          id: p.id,
          name: p.name,
          description: p.description ?? null,
          price: p.price,
          category: p.category,
          imageUrl: p.imageUrl ?? null,
          stockToday: p.stockToday,
          isEditable: p.isEditable,
          optionGroups: p.optionGroups,
        },
      }));
      const debugMeta = { total: allProducts.length, withPriority: withPriority.length, passed: upsellProducts.length };
      productsCacheRef.current = { rid, upsells, ts: Date.now(), debugMeta };
      return { upsells, source: 'products', ...debugMeta };
    } catch (e) {
      console.warn('Products API failed:', e);
      return null;
    }
  }, []);

  const fetchOrder = useCallback(async (socket?: ReturnType<typeof io> | null, opts?: { forceUpsells?: boolean }) => {
    if (!qrCode || fetchingRef.current) return;
    fetchingRef.current = true;
    const debugParam = debugMode ? '&debug=1' : '';
    const url = `${API_URL || ''}/api/orders/qr/${qrCode}?_=${Date.now()}${debugParam}`;
    try {
      const { data } = await axios.get(url, {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      const rid = data.restaurantId || data.restaurant?.id || null;
      restaurantIdRef.current = rid;

      // If backend signals order is done on a cold load (user opened a finished order's QR),
      // redirect to home so stale completed orders don't re-open on PWA launch.
      // EXCEPTION: a COMPLETED order that hasn't been reviewed yet stays on the page so
      // the customer can still rate it after pickup. Cancelled and already-reviewed orders
      // still redirect (nothing actionable left).
      // But if the user was already watching the order (hasLoadedOnceRef.current = true),
      // let setOrder + the completion screen handle the transition — navigating away here
      // would wipe the "order ready / completed" screen that the socket already showed.
      const isUnreviewedCompleted = data.status === 'COMPLETED' && !data.reviewed;
      if (data.completed && !isUnreviewedCompleted && !hasLoadedOnceRef.current) {
        if (isActiveRef.current) navigate('/');
        return;
      }

      let upsells: UpsellItem[] = data.upsells || [];
      let upsellsSource: 'products' | 'order' = 'order';
      let productsTotal: number | undefined;
      let productsWithPriority: number | undefined;
      let productsPassedFilter: number | undefined;

      if (rid) {
        const maxUpsells = Math.max(data.restaurant?.preferences?.maxUpsells ?? 12, 12);
        const result = await fetchProducts(rid, maxUpsells);
        // Only use fetchProducts result if it actually returned items.
        // Restaurants without any product flagged `upsellPriority>0` would
        // otherwise wipe out the ML-derived `data.upsells` and show "no upsells".
        if (result && result.upsells.length > 0) {
          upsells = result.upsells;
          upsellsSource = result.source;
          productsTotal = result.total;
          productsWithPriority = result.withPriority;
          productsPassedFilter = result.passed;
        } else if (result) {
          // Keep ML upsells; record diagnostic counts only.
          productsTotal = result.total;
          productsWithPriority = result.withPriority;
          productsPassedFilter = result.passed;
        }
      }

      // Persist active order so PWA can restore it on cold start (non-auth users)
      if (!['COMPLETED', 'CANCELLED'].includes(data.status) && qrCode) {
        localStorage.setItem('olso_active_order', qrCode);
      }

      // Mark finished orders so PWA doesn't reopen them on next launch
      if (['COMPLETED', 'CANCELLED'].includes(data.status) && qrCode) {
        localStorage.removeItem('olso_active_order');
        localStorage.setItem(`olso_order_done_${qrCode}`, '1');
        // In standalone PWA, if order is already finished on first load,
        // redirect to home instead of showing stale completed order
        const isStandalone =
          (window.navigator as { standalone?: boolean }).standalone === true ||
          window.matchMedia('(display-mode: standalone)').matches;
        if (isStandalone && !hasLoadedOnceRef.current && !(data.status === 'COMPLETED' && !data.reviewed)) {
          if (isActiveRef.current) navigate('/');
          return;
        }
      }

      // Keep the already-shown upsell/recommendation cards stable across the 5s
      // polling so they don't visibly reshuffle every few seconds. Adopt a fresh
      // set only on the first load, an explicit refresh, or an `upsells-refresh`
      // event (forceUpsells). Mirrors the socket handler's preservation.
      setOrder((prev) => {
        if (opts?.forceUpsells || !prev) return { ...data, upsells };
        return {
          ...data,
          upsells: prev.upsells?.length ? prev.upsells : upsells,
          recommendations: prev.recommendations?.r1 ? prev.recommendations : data.recommendations,
          offers: prev.offers ?? data.offers,
        };
      });
      setRepeatItems(data.repeatItems || []);
      setSocialProofCount(data.socialProofCount || 0);
      if (sessionStorage.getItem(`repeat-dismissed-${data.id}`)) {
        // flag is read by component from sessionStorage
      }
      setUpsellsLoading(false);
      hasLoadedOnceRef.current = true;
      setIsOffline(false);
      setLoadError(null); // a successful poll clears any prior transient error

      const prevStatus = prevStatusRef.current;
      const newStatus = data.status;
      if (prevStatus === null) {
        prevStatusRef.current = newStatus;
      } else if (prevStatus !== newStatus) {
        prevStatusRef.current = newStatus;
        setStatusChanged(newStatus);
        if (newStatus === 'READY') {
          triggerReadyEffect();
          setShowReadyFlash(true);
          setTimeout(() => setShowReadyFlash(false), 3500);
        }
        if (newStatus === 'PREPARING') triggerPreparingEffect();
        setTimeout(() => setStatusChanged(null), 3000);
      }

      orderIdRef.current = data.id;
      if (socket && typeof socket.emit === 'function') {
        socket.emit('join-order', data.id);
        socket.emit('join', `venue-session:${data.id}`);
      }
      if (rid && socket && typeof socket.emit === 'function') {
        socket.emit('join-restaurant', rid);
      }
      if (debugMode) {
        setDebugInfo({
          lastFetch: new Date().toISOString(),
          apiUrl: url,
          productPriorities: (data as { _debug?: { productPriorities?: { name: string; upsellPriority: number }[] } })._debug?.productPriorities,
          prioritiesError: undefined,
          upsellsSource,
          productsTotal: rid ? productsTotal : undefined,
          productsWithPriority: rid ? productsWithPriority : undefined,
          productsPassedFilter: rid ? productsPassedFilter : undefined,
        });
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setOrder(null);
        setLoadError(null);
      } else if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
        setIsOffline(true);
        // Only show error screen if we have no cached data to show.
        if (!hasLoadedOnceRef.current) {
          setLoadError(
            import.meta.env.VITE_API_URL
              ? 'Cannot reach server. Check your connection.'
              : 'Cannot reach server. Use the same WiFi as the restaurant.'
          );
        }
      } else if (!hasLoadedOnceRef.current) {
        // Only blow away the page on an INITIAL load failure. Mid-session (we
        // already have the order), a transient error (e.g. a 500 during a deploy)
        // is swallowed — keep showing the cached order; the next poll recovers.
        const status = err.response?.status;
        const msg = err.response?.data?.error || err.message || 'Failed to load';
        const apiHint = !API_URL && /railway|vercel|netlify/.test(window.location.hostname)
          ? ' Set VITE_API_URL to your backend URL (e.g. https://your-backend.up.railway.app) in Railway variables and redeploy.'
          : '';
        setLoadError(status ? `${msg} (${status})${apiHint}` : `${msg}${apiHint}`);
      } else {
        setIsOffline(true); // transient mid-session error — show the subtle offline hint, keep the order
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCode, debugMode, fetchProducts, navigate]);

  // Keep fetchOrderRef in sync so online listener always calls latest version
  useEffect(() => {
    fetchOrderRef.current = fetchOrder;
  }, [fetchOrder]);

  // Track online/offline state; retry immediately when connection is restored
  useEffect(() => {
    const goOnline = () => {
      setIsOffline(false);
      fetchOrderRef.current?.(socketRef.current);
    };
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Pre-warm Firebase so auth buttons are ready on first tap (fixes iOS double-tap)
  useEffect(() => { getFirebaseAuth(); }, []);

  // Ensure _ct persistent token is in URL for PWA "Add to Home Screen"
  useEffect(() => {
    if (!isAuthenticated) { addDebug('[_ct] not auth'); return; }
    const url = new URL(window.location.href);
    if (url.searchParams.has('_ct')) { addDebug('[_ct] already in URL '); return; }
    const auth = getStoredAuth();
    if (!auth?.token) { addDebug('[_ct] no stored token'); return; }
    const apiBase = API_URL || '';
    addDebug(`[_ct] posting to ${apiBase}/api/auth/customer/persistent-token`);
    axios.post(`${apiBase}/api/auth/customer/persistent-token`, null, {
      headers: { Authorization: `Bearer ${auth.token}` },
    }).then(({ data }) => {
      if (data.persistentToken) {
        const u = new URL(window.location.href);
        u.searchParams.set('_ct', data.persistentToken);
        window.history.replaceState(null, '', u.toString());
        addDebug('[_ct] embedded ');
      } else {
        addDebug('[_ct] no token in response');
      }
    }).catch((err) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      addDebug(`[_ct] FAIL: ${status || err?.message}`);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Apply language from QR scan if provided by CustomerDisplay
  useEffect(() => {
    const lang = searchParams.get('lang');
    if (lang === 'ru' || lang === 'en' || lang === 'kk') {
      setStoredLang(lang);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Socket setup, order fetching and polling
  useEffect(() => {
    if (!qrCode) return;

    const socketUrl =
      API_URL ||
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:3001'
        : `${window.location.protocol}//${window.location.hostname}:3001`);

    const socket = io(socketUrl, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    const safeEmit = (ev: string, ...args: unknown[]) => {
      if (socket && typeof socket.emit === 'function') socket.emit(ev, ...args);
    };
    // Fetch venue orders — used on reconnect and visibility return to
    // catch any `venue-order-updated` event that was missed while the
    // socket was disconnected (iOS PWA backgrounding, network drops).
    const refetchVenueOrdersNow = () => {
      const oid = orderIdRef.current;
      if (!oid) return;
      const base = API_URL || '';
      axios.get(`${base}/api/orders/${oid}/venue-orders`)
        .then(r => { if (Array.isArray(r.data)) setVenueOrders(r.data); })
        .catch(() => { /* ignore */ });
    };

    socket.on('connect', () => {
      if (orderIdRef.current) {
        safeEmit('join-order', orderIdRef.current);
        safeEmit('join', `venue-session:${orderIdRef.current}`);
      }
      if (restaurantIdRef.current) safeEmit('join-restaurant', restaurantIdRef.current);
      // Sync venue orders after (re)connect — covers events missed while offline
      refetchVenueOrdersNow();
    });

    socket.on('venue-order-updated', (data: { venueOrderId: string; status: string; restaurantName?: string; restaurantId?: string }) => {
      setVenueOrders(prev => {
        const idx = prev.findIndex(vo => vo.id === data.venueOrderId);
        if (idx === -1) {
          // Unknown venue order — refetch all from DB
          if (orderIdRef.current) {
            const base = API_URL || '';
            axios.get(`${base}/api/orders/${orderIdRef.current}/venue-orders`)
              .then(r => setVenueOrders(r.data))
              .catch(() => {});
          }
          return prev;
        }
        const updated = [...prev];
        updated[idx] = { ...updated[idx], status: data.status };
        return updated;
      });
    });

    socket.on('order-updated', (o: Order) => {
      const prevStatus = prevStatusRef.current;
      const newStatus = o.status;

      setOrder((prev) => mergeOrderUpdate(prev, o));
      if (o.restaurantId) safeEmit('join-restaurant', o.restaurantId);

      if (prevStatus && prevStatus !== newStatus) {
        setStatusChanged(newStatus);
        if (newStatus === 'READY') {
          triggerReadyEffect();
          setShowReadyFlash(true);
          setTimeout(() => setShowReadyFlash(false), 3500);
        }
        if (newStatus === 'PREPARING') triggerPreparingEffect();
        if (
          newStatus === 'PREPARING' &&
          !sessionStorage.getItem(`preparing-shown-${o.id}`)
        ) {
          sessionStorage.setItem(`preparing-shown-${o.id}`, '1');
          setShowPreparingModal(true);
          navigator.vibrate?.(200);
        }
        // Mark order as done so PWA doesn't reopen it on next launch
        if ((newStatus === 'COMPLETED' || newStatus === 'CANCELLED') && qrCode) {
          localStorage.setItem(`olso_order_done_${qrCode}`, '1');
        }
        setTimeout(() => setStatusChanged(null), 3000);
      }
      prevStatusRef.current = newStatus;
    });

    socket.on('upsells-refresh', () => {
      productsCacheRef.current = null;
      fetchOrder(socket, { forceUpsells: true });
    });

    socket.on('payment-confirmed', (data: { paymentId: string; orderId: string; amount: number }) => {
      onPaymentConfirmed({ amount: data.amount });
      fetchOrder(socket);
    });

    socket.on('payment-failed', (data: { reason: string }) => {
      onPaymentFailed(data.reason);
    });

    socket.on('order-refunded', (data: { paymentId: string; amount: number }) => {
      setRefundAmount(data.amount ?? null);
      setShowRefundSheet(true);
    });

    fetchOrder(socket);

    const poll = setInterval(() => fetchOrder(socket), POLL_INTERVAL);
    return () => {
      socketRef.current = null;
      socket.off('payment-confirmed');
      socket.off('payment-failed');
      socket.off('order-refunded');
      socket.close();
      clearInterval(poll);
    };
  }, [qrCode, fetchOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load venue sub-orders from DB when order is known (replaces localStorage + polling)
  useEffect(() => {
    if (!order?.id) return;
    const base = API_URL || '';
    axios.get(`${base}/api/orders/${order.id}/venue-orders`)
      .then(r => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          setVenueOrders(r.data);
        }
      })
      .catch(() => {
        // Fallback: try localStorage cache if DB fetch fails
        try {
          const stored = localStorage.getItem(`olso_venue_orders_${order.id}`);
          if (stored) {
            const parsed: VenueOrder[] = JSON.parse(stored);
            setVenueOrders(parsed.slice(0, 5));
          }
        } catch { /* ignore */ }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  // Claim order for authenticated customer (links customerId → enables friend notifications)
  useEffect(() => {
    if (!order || !isAuthenticated || claimedRef.current) return;
    if (order.customerId) { claimedRef.current = true; return; } // already linked
    claimedRef.current = true;
    const storedAuth = getStoredAuth();
    if (!storedAuth?.token) return;
    axios.post(
      `${API_URL || ''}/api/orders/claim`,
      { orderId: order.id },
      { headers: { Authorization: `Bearer ${storedAuth.token}` } },
    ).then((r) => {
      console.log('[claim]', r.data);
    }).catch((err) => {
      console.warn('[claim] failed:', err?.response?.data || err.message);
    });
  }, [order, isAuthenticated]);

  // Wake Lock — keep screen on while waiting
  useEffect(() => {
    if (!order || !['PENDING', 'PREPARING', 'READY'].includes(order.status)) return;
    const acquire = async () => {
      if (!('wakeLock' in navigator)) return;
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      } catch { /* not supported or not allowed */ }
    };
    acquire();
    const onVisible = () => { if (document.visibilityState === 'visible') acquire(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [order?.status]);

  // Reconnect socket + refetch order when app returns from background
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const sock = socketRef.current;
      if (sock && !sock.connected) {
        sock.connect();
      }
      fetchOrderRef.current?.(sock);
      // Also resync venue sub-orders — iOS PWA may have missed
      // `venue-order-updated` events while suspended (PREPARING→READY, etc.)
      const oid = orderIdRef.current;
      if (oid) {
        const base = API_URL || '';
        axios.get(`${base}/api/orders/${oid}/venue-orders`)
          .then(r => { if (Array.isArray(r.data)) setVenueOrders(r.data); })
          .catch(() => { /* ignore */ });
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Show PREPARING modal if user returns to tab after status already changed
  useEffect(() => {
    const handleVisibility = () => {
      if (
        document.visibilityState === 'visible' &&
        order?.status === 'PREPARING' &&
        !sessionStorage.getItem(`preparing-shown-${order.id}`)
      ) {
        sessionStorage.setItem(`preparing-shown-${order.id}`, '1');
        setShowPreparingModal(true);
        navigator.vibrate?.(200);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [order?.status, order?.id]);

  const handleRefresh = useCallback(() => {
    productsCacheRef.current = null;
    fetchOrder(socketRef.current, { forceUpsells: true });
  }, [fetchOrder]);

  const openVenueDetail = async (vo: VenueOrder) => {
    setVenueDetailId(vo.id);
    setVenueDetailData(null);
    setVenueDetailLoading(true);
    try {
      const base = API_URL || '';
      const res = await axios.get(`${base}/api/orders/qr/${vo.qrCode}`);
      setVenueDetailData({
        items: res.data.items || [],
        restaurantName: vo.restaurantName,
        status: res.data.status || vo.status,
      });
    } catch {
      setVenueDetailData({
        items: [],
        restaurantName: vo.restaurantName,
        status: vo.status,
      });
    } finally {
      setVenueDetailLoading(false);
    }
  };

  return {
    order,
    setOrder,
    loading,
    loadError,
    isOffline,
    upsellsLoading,
    debugInfo,
    statusChanged,
    showReadyFlash,
    setShowReadyFlash,
    showPreparingModal,
    setShowPreparingModal,
    repeatItems,
    socialProofCount,
    venueOrders,
    setVenueOrders,
    venueDetailId,
    setVenueDetailId,
    venueDetailData,
    setVenueDetailData,
    venueDetailLoading,
    setVenueDetailLoading,
    showRefundSheet,
    setShowRefundSheet,
    refundAmount,
    socketRef,
    handleRefresh,
    fetchOrder,
    openVenueDetail,
  };
}
